import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "listbox", "hidden", "clearButton", "option", "loading"]
  static values = { 
    open: Boolean,
    dynamic: Boolean,
    url: String,
    param: String
  }

  initialize() {
    this.clickOutside = this.clickOutside.bind(this)
    this.handleCustomerSelected = this.handleCustomerSelected.bind(this)
    this.boundHandleCustomerCreated = this.handleCustomerCreated.bind(this)
  }

  connect() {
    console.log("HW Combobox connected", {
      element: this.element,
      listbox: this.hasListboxTarget ? this.listboxTarget : 'missing',
      input: this.hasInputTarget ? this.inputTarget : 'missing',
      hidden: this.hasHiddenTarget ? this.hiddenTarget : 'missing',
      url: this.hasUrlValue ? this.urlValue : 'missing',
      param: this.hasParamValue ? this.paramValue : 'missing'
    })
    document.addEventListener("mousedown", this.clickOutside)
    document.addEventListener("customer-selected", this.handleCustomerSelected)
    document.addEventListener("customer:created", this.boundHandleCustomerCreated)
  }

  disconnect() {
    document.removeEventListener("mousedown", this.clickOutside)
    document.removeEventListener("customer-selected", this.handleCustomerSelected)
    document.removeEventListener("customer:created", this.boundHandleCustomerCreated)
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  open() {
    this.openValue = true
    if (this.hasListboxTarget) {
      this.listboxTarget.classList.remove("hidden")
    }
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }

  close() {
    this.openValue = false
    if (this.hasListboxTarget) {
      this.listboxTarget.classList.add("hidden")
    }
  }

  clear() {
    this.inputTarget.value = ""
    this.hiddenTarget.value = ""
    this.close()
  }

  startLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
    this.open()
  }

  focus(event) {
    if (!this.openValue) {
      // Only trigger initial search if we have a URL configured
      if (!this.inputTarget.value && this.hasUrlValue) {
        const searchEvent = new Event('input', { bubbles: true })
        this.inputTarget.dispatchEvent(searchEvent)
      }
      this.open()
    }
  }

  search(event) {
    console.log("Search triggered", {
      hasUrl: this.hasUrlValue,
      url: this.urlValue,
      param: this.paramValue,
      query: event.target.value
    })

    if (!this.hasUrlValue) return
    const query = event.target.value.toLowerCase()
    
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
    
    // Use data attributes for frame ID and URL
    const frameId = this.element.querySelector("[id$='_search_results']").id
    console.log("Frame detection", {
      frameElement: this.element,
      frameQuery: "[id$='_search_results']",
      foundFrameId: frameId
    })
    const frame = document.getElementById(frameId)
    
    if (!frame) {
      console.error("Frame not found:", frameId)
      return
    }
    
    if (frame) {
      const url = `${this.urlValue}?${this.paramValue}=${encodeURIComponent(query)}&format=turbo_stream`
      console.log("Fetching URL:", url)
      
      fetch(url, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })
        .then(response => {
          console.log("Response status:", response.status)
          return response.text()
        })
        .then(html => {
          console.log("Received HTML:", html.substring(0, 100)) // Log first 100 chars
          frame.innerHTML = html
          if (this.hasLoadingTarget) {
            this.loadingTarget.classList.add("hidden")
          }
          this.open()
        })
        .catch(error => {
          console.error("Fetch error:", error)
        })
    }
  }

  filterOptions(query) {
    let hasVisibleOptions = false
    this.optionTargets.forEach(option => {
      const label = option.dataset.hwComboboxLabel.toLowerCase()
      const isVisible = label.includes(query)
      option.classList.toggle("hidden", !isVisible)
      if (isVisible) hasVisibleOptions = true
    })
    
    if (hasVisibleOptions) {
      this.open()
    } else {
      this.close()
    }
  }

  selectOption(event) {
    const option = event.currentTarget
    const value = option.dataset.hwComboboxValue
    const label = option.dataset.hwComboboxLabel

    // Update the hidden input with the selected value
    this.hiddenTarget.value = value
    
    // Update the visible input with the selected label
    this.inputTarget.value = label
    
    // Close the dropdown
    this.close()
    
    // Dispatch change event on hidden input
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  highlightOption(event) {
    const option = event.currentTarget
    this.optionTargets.forEach(o => o.classList.remove("bg-primary-600", "text-white"))
    option.classList.add("bg-primary-600", "text-white")
  }

  keydown(event) {
    if (!this.openValue) return

    // Handle escape key
    if (event.key === "Escape") {
      this.close()
      return
    }

    // Handle arrow keys
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const options = Array.from(this.listboxTarget.querySelectorAll("li:not(.hidden)"))
      if (options.length === 0) return

      const currentOption = this.listboxTarget.querySelector(".bg-primary-600")
      const currentIndex = currentOption ? options.indexOf(currentOption) : -1
      const direction = event.key === "ArrowDown" ? 1 : -1

      this.navigateOptions(direction, options, currentIndex)
    }

    // Handle enter key
    if (event.key === "Enter") {
      event.preventDefault()
      const highlightedOption = this.listboxTarget.querySelector(".bg-primary-600")
      if (highlightedOption) {
        highlightedOption.click()
      }
    }
  }

  navigateOptions(direction, options, currentIndex) {
    let newIndex = currentIndex + direction
    if (newIndex < 0) newIndex = options.length - 1
    if (newIndex >= options.length) newIndex = 0

    options.forEach(o => o.classList.remove("bg-primary-600", "text-white"))
    options[newIndex].classList.add("bg-primary-600", "text-white")
    options[newIndex].scrollIntoView({ block: "nearest" })
  }

  handleCustomerSelected(event) {
    // "customer-selected"/"customer:created" are dispatched on `document`, so
    // every hw-combobox instance on the page hears them -- e.g. the legacy
    // reservation form's rental combobox lives alongside the customer
    // combobox on the same page. Without this guard, creating/selecting a
    // customer would also stomp the rental hidden field's value.
    if (!this.#isQuickAddCombobox()) return

    const { customerId, customerName } = event.detail ?? {}
    this.hiddenTarget.value = customerId
    this.inputTarget.value = customerName
    this.close()
  }

  handleCustomerCreated(event) {
    if (!this.#isQuickAddCombobox()) return

    const customerId = event.target.dataset.customerId
    const customerName = event.target.dataset.customerName

    // Update the hidden input with the new customer's ID
    this.hiddenTarget.value = customerId

    // Update the visible input with the customer's name
    this.inputTarget.value = customerName

    // Close the dropdown
    this.close()

    // Collapse the Quick Add form back to its initial state
    const quickForm = this.element.querySelector("[data-quick-customer-target='form']")
    if (quickForm) {
      quickForm.classList.add("hidden")
      quickForm.querySelector("form")?.reset()
      quickForm.querySelectorAll("[data-quick-customer-target='field']").forEach(field => {
        field.removeAttribute("required")
      })
    }

    const toggleIcon = this.element.querySelector("[data-quick-customer-target='toggleIcon']")
    if (toggleIcon) {
      toggleIcon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />'
    }

    // Trigger a search to refresh the customer list
    const searchEvent = new Event('input', { bubbles: true })
    this.inputTarget.dispatchEvent(searchEvent)
  }

  #isQuickAddCombobox() {
    return (this.element.dataset.controller || "").split(" ").includes("quick-customer")
  }
}
